import React, { Suspense, useEffect } from 'react';

import { ThemeProvider } from '@mui/material';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import BannerContainer from 'components/BannerContainer';
import ConnectedAudioPlayer from 'components/ConnectedAudioPlayer';
import StoryListenHandler from 'components/ConnectedAudioPlayer/StoryListenHandler';
import FullScreenLogo from 'components/FullScreenLogo';
import LanguageSwitch from 'components/LanguageSwitch';
import MatomoTracker from 'components/MatomoTracker';
import ProcessesWrapper from 'components/Processes/ProcessesWrapper';
import PromoCountdownBanner from 'components/PromoCountdownBanner';
import SiteMetaInformation from 'components/SiteMetaInformation';
import TrialProgressBarWithReactivation from 'components/TrialProgressBarWithReactivation';

import history from 'config/history';

import App from 'containers/App';
import { theme } from 'containers/Femtasy/materialTheme';
import KlaviyoWrapper from 'containers/KlaviyoWrapper';

import setupHeadersInterceptor from 'helpers/axiosHeadersInterceptor';
import { setupPWAInstallPrompt } from 'helpers/pwa/installation';

import useSaveGclidInCookies from 'hooks/useSaveGclidInCookies';
import withGraphQL from 'hooks/withGraphQL';

import configureStore from 'store/configureStore';
import { logout } from 'store/modules/auth';

import 'styles/main.scss';
import '../../index.css';

const { store, persistor } = configureStore();

setupPWAInstallPrompt(window);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;

// Inferred type
export type AppDispatch = typeof store.dispatch;

window.addEventListener('graphql_unauthorized_error', () => {
  // @ts-ignore
  store.dispatch(logout({ invalidateToken: false }));
});

// Dynamically detect changes to the scren height and set it as a global variable in CSS
const updateAppHeight = () => {
  const doc = document.documentElement;

  doc.style.setProperty('--app-height', `${window.innerHeight}px`);
};

window.addEventListener('resize', updateAppHeight);

setTimeout(updateAppHeight, 0);

const AppWrapper = () => {
  useSaveGclidInCookies();

  return (
    // @ts-ignore:next-line Until https://github.com/jonkoops/matomo-tracker/commit/8b5488028678e4c28f850312b55b7e7dfffb4842 is integrated into our fork
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ConnectedAudioPlayer />
        <Router history={history}>
          <MatomoTracker />
          <LanguageSwitch>
            {(locale: string, messages: Record<string, string>) => {
              return (
                <IntlProvider
                  locale={locale}
                  messages={messages}
                  textComponent="span"
                >
                  <KlaviyoWrapper />
                  <SiteMetaInformation />
                  <StoryListenHandler />
                  <PromoCountdownBanner />
                  <TrialProgressBarWithReactivation />
                  <BannerContainer />
                  <ProcessesWrapper />
                  <ThemeProvider theme={theme}>
                    <Suspense fallback={<FullScreenLogo />}>
                      <App />
                    </Suspense>
                  </ThemeProvider>
                </IntlProvider>
              );
            }}
          </LanguageSwitch>
        </Router>
      </PersistGate>
    </Provider>
  );
};

const setupHeadersInterceptorHook = () => {
  setupHeadersInterceptor({
    authHeaders: store.getState().auth.headers,
    dispatch: store.dispatch,
    history,
  });
};

const Femtasy = () => {
  useEffect(setupHeadersInterceptorHook, []);

  return <AppWrapper />;
};

export default withGraphQL(Femtasy);
